import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  type DisplayEntry,
  type GameMetadata,
  type ProductConfig,
  type PublicProductConfig,
} from "./api";
import { humanKey } from "./format";

const BUILD_GOOGLE_CLIENT_ID: string =
  import.meta.env["VITE_GOOGLE_CLIENT_ID"] ??
  "replace-me.apps.googleusercontent.com";
const BUILD_AUTH_HOSTED_DOMAIN: string =
  import.meta.env["VITE_AUTH_HOSTED_DOMAIN"] ?? "example.org";

const FALLBACK: ProductConfig = {
  schemaVersion: 1,
  product: {
    name: "Game Analytics",
    tagline: "Live game answers — minutes fresh, not a day late.",
    icon: "/brand/example-icon.svg",
    lockup: "/brand/example-lockup.svg",
    documentationUrl: "https://example.org/docs",
    supportUrl: "https://example.org/support",
  },
  auth: {
    provider: "google",
    hostedDomain: BUILD_AUTH_HOSTED_DOMAIN,
    // The production web build already receives this public value. Keeping it
    // as the floor means a transient `/v1/config` failure cannot remove the
    // sign-in button that worked before runtime branding was introduced.
    googleClientId: BUILD_GOOGLE_CLIENT_ID,
  },
  games: {},
};

interface ProductConfigContextValue {
  config: ProductConfig;
  configurationStatus: "loading" | "ready" | "failed";
  configurationError: string | null;
  metadataStatus: "idle" | "loading" | "ready" | "failed";
  metadataError: string | null;
  retryConfiguration(): Promise<void>;
  loadMetadata(): Promise<void>;
}

const ProductConfigContext = createContext<ProductConfigContextValue | null>(
  null,
);

function withPublicConfig(
  current: ProductConfig,
  next: PublicProductConfig,
): ProductConfig {
  return { ...next, games: current.games };
}

export function ProductConfigProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const [config, setConfig] = useState<ProductConfig>(FALLBACK);
  const [configurationStatus, setConfigurationStatus] =
    useState<ProductConfigContextValue["configurationStatus"]>("loading");
  const [configurationError, setConfigurationError] = useState<string | null>(
    null,
  );
  const [metadataStatus, setMetadataStatus] =
    useState<ProductConfigContextValue["metadataStatus"]>("idle");
  const [metadataError, setMetadataError] = useState<string | null>(null);

  const retryConfiguration = useCallback(async (): Promise<void> => {
    setConfigurationStatus("loading");
    setConfigurationError(null);
    try {
      const next = await api.configuration();
      setConfig((current) => withPublicConfig(current, next));
      setConfigurationStatus("ready");
    } catch (error) {
      setConfigurationStatus("failed");
      setConfigurationError(
        error instanceof Error
          ? error.message
          : "Neulytics could not load sign-in settings.",
      );
    }
  }, []);

  useEffect(() => {
    void retryConfiguration();
  }, [retryConfiguration]);

  useEffect(() => {
    document.title = config.product.name;
    const theme = config.product.theme;
    const root = document.documentElement;
    const variables = {
      "--action": theme?.action,
      "--series-1": theme?.series1,
      "--series-2": theme?.series2,
      "--series-3": theme?.series3,
    };
    for (const [key, value] of Object.entries(variables)) {
      if (value === undefined) root.style.removeProperty(key);
      else root.style.setProperty(key, value);
    }
  }, [config.product.name, config.product.theme]);

  const loadMetadata = useCallback(async (): Promise<void> => {
    setMetadataStatus((current) => (current === "ready" ? "ready" : "loading"));
    try {
      const next = await api.metadata();
      setConfig(next);
      setMetadataStatus("ready");
      setMetadataError(null);
    } catch (error) {
      setMetadataStatus("failed");
      setMetadataError(
        error instanceof Error
          ? error.message
          : "Neulytics could not load display names.",
      );
    }
  }, []);

  const value = useMemo(
    () => ({
      config,
      configurationStatus,
      configurationError,
      metadataStatus,
      metadataError,
      retryConfiguration,
      loadMetadata,
    }),
    [
      config,
      configurationError,
      configurationStatus,
      loadMetadata,
      metadataError,
      metadataStatus,
      retryConfiguration,
    ],
  );

  return (
    <ProductConfigContext.Provider value={value}>
      {children}
    </ProductConfigContext.Provider>
  );
}

export function useProductConfig(): ProductConfigContextValue {
  const value = useContext(ProductConfigContext);
  if (value === null) {
    throw new Error("useProductConfig must be inside ProductConfigProvider");
  }
  return value;
}

export interface DisplayName {
  name: string;
  mapped: boolean;
  key: string;
}

export function displayName(
  entries: Record<string, DisplayEntry> | undefined,
  key: string,
): DisplayName {
  const entry = entries?.[key];
  return {
    name: entry?.name ?? humanKey(key),
    mapped: entry !== undefined,
    key,
  };
}

export function useGameMetadata(gameId: string): {
  game: GameMetadata | undefined;
  display: DisplayName;
} {
  const { config } = useProductConfig();
  const game = config.games[gameId];
  return {
    game,
    display: {
      name: game?.name ?? humanKey(gameId),
      mapped: game !== undefined,
      key: gameId,
    },
  };
}

export function Unmapped({ value }: { value: DisplayName }): ReactNode {
  /*
   * NO BADGE. "Configure a display name" is an operator task, not something a
   * reader needs stamped beside every value they are trying to read.
   *
   * A game with nothing in its manifest -- which is the normal state before
   * anyone fills one in -- put that badge on EVERY id in EVERY table, next to
   * names like `IronMaul` that already read perfectly well. It made the data
   * harder to read in order to nag about something the reader usually cannot
   * fix, and it nagged hardest exactly where it was least useful.
   *
   * The information is not lost: the raw key is still on hover, and Data
   * Health counts the missing names in one place, which is where a task for
   * an operator belongs.
   */
  return (
    <span
      title={value.mapped ? undefined : `No display name for: ${value.key}`}
    >
      {value.name}
    </span>
  );
}
